<template>
  <div>
    <tool-bar :title="title" subTitle=""></tool-bar>
    <loading :loading="loading"></loading>
    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false">
    </error-alert-item>

    <activity-redirect-item
      :loan-tasks="tasks"
      :current-loan-task="currentTaskName"
      :is-loan-task="true"
      :redirect-activity="redirectActivity"
    ></activity-redirect-item>

    <refresh-item
      :show="showRefreshItem"
      @onRefresh="fetchInitialData"
    ></refresh-item>

    <stage-comment-item></stage-comment-item>

    <single-message-dialog
      :message="goToHomePageMessage"
      :show="showGoToHomePageDialog"
      button-text="Go to Home Page"
      @onContinue="onGoToHomePage"
    ></single-message-dialog>

    <div v-if="lenderPlatformMessage"
         class="margin_top_20 margin-left-10 margin-right-10">
      <weex-text
        class="font-size-sm-base text-color-black"
        v-html="lenderPlatformMessage"
      ></weex-text>
    </div>

    <div class="margin_top_40" v-else-if="showUI">
      <div class="input_container">
        <weex-text class="input_label">Total amount to be collected from customer (Incl. Processing Fees)</weex-text>
        <div class="item-price-input-container read-only">
          <input
            :readonly="true"
            class="item-price-label-input"
            :value="rupeeSign" />

          <input
            :readonly="true"
            class="item-price-input read-only"
            :value="downpaymentAmount" />
        </div>
      </div>

      <div class="margin-top-30">
        <div v-if="onlinePaymentAllowed">
          <wxc-button
            text="Pay Online"
            size="small"
            class="horizontal_center margin-top-10"
            type="blue"
            @wxcButtonClicked="onPayOnlineClicked"
          ></wxc-button>

          <weex-text
            v-if="onlinePaymentStatus"
            class="font-size-sm text-align-center text-color-green">
            Payment Success
          </weex-text>
        </div>


        <div v-if="onlinePaymentAllowed && manualPaymentAllowed" class="margin-top-10">
          <weex-text class="item-sm-description text-color-gray">-OR-</weex-text>
        </div>

        <div
          v-if="manualPaymentAllowed"
          class="ezcred-checkbox-div margin-top-10 horizontal_center"
          style="max-width: 170px"
          @click="onPaymentWithCashConsentSelected">
          <weex-image
            :src="paymentWithCashCheckboxLink"
            class="ezcred-checkbox-image"
          ></weex-image>
          <weex-text class="ezcred-checkbox-text">Payment with Cash</weex-text>
        </div>
      </div>

      <wxc-button
        text="Next"
        class="button_horizontal_center margin_top_40 margin-bottom-20"
        type="blue"
        @wxcButtonClicked="onSubmit"
      ></wxc-button>
    </div>
  </div>
</template>

<script>
import ToolBar from "../layout/ToolBar";
import ActivityRedirectItem from "../layout/ActivityRedirectItem";
import ErrorAlertItem from "../layout/ErrorAlertItem";
import Loading from "../layout/Loading";
import StageCommentItem from "../layout/StageCommentItem";
import RefreshItem from "../layout/RefreshItem";
import WxcButton from "../../../weex-ui/packages/wxc-button/index";
import LoanScopeDataHolder from "../../data/model/LoanScopeDataHolder";
import DataSource from "../../data/DataSource";
import PrefManager from "../../data/model/PrefManager";
import ActivityNavigator, { Activities } from "../../utils/ActivityNavigator";
import { MessageConstants } from "../../utils/MessageConstantUtil";
import LoanTasksActivityNavigator from "../../utils/LoanTasksActivityNavigator";
import { ImageResourcesUtil } from "../../utils/ImageResourcesUtil";
import LoanTasksUtility from "../../utils/LoanTasksUtility";
import AlertService from "../core/alert/AlertService";
import SingleMessageDialog from "../dialogs/SingleMessageDialog";
import { environment } from "../../environments/environment";

const RUPEE = "\u20B9";

export default {
  name: "Downpayment",
  components: {
    ActivityRedirectItem,
    ToolBar,
    WxcButton,
    ErrorAlertItem,
    Loading,
    SingleMessageDialog,
    StageCommentItem,
    RefreshItem
  },

  data: () => ({
    title: "Downpayment",
    loading: false,
    errorAlertEvent: false,
    networkError: {},
    downpaymentAmount: 0,
    showUI: false,
    onlinePaymentAllowed: false,
    manualPaymentAllowed: false,
    onlinePaymentStatus: false,
    cashPaymentStatus: false,
    showGoToHomePageDialog: false,
    goToHomePageMessage: "",
    showRefreshItem: false,
    lenderPlatformMessage: "",
    razorpayScriptLoaded: false,
    onlinePaymentConfiguration: {}
  }),

  methods: {
    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    showToast(message) {
      AlertService.showToast(message);
    },

    onGoToHomePage() {
      ActivityNavigator.navigateTo(Activities.LOAN_DETAILS);
    },

    onPaymentWithCashConsentSelected() {
      this.cashPaymentStatus = !this.cashPaymentStatus;
    },

    onPayOnlineClicked() {
      this.loading = true;
      DataSource.getDownpaymentConfiguration(LoanScopeDataHolder.getLoanId())
        .then(downpaymentConfigRes => {
          this.loading = false;
          if (downpaymentConfigRes.errorOccurred) {
            this.showError(downpaymentConfigRes.data);
          } else {
            this.onlinePaymentConfiguration = downpaymentConfigRes.data
              ? downpaymentConfigRes.data
              : {};

            if (environment.razorpayVendorName === this.onlinePaymentConfiguration.vendorName) {
              if (this.onlinePaymentConfiguration.orderId
                && this.onlinePaymentConfiguration.key
                && this.onlinePaymentConfiguration.amount
              ) {
                this.loadRazorpayScriptIfNeededOrContinueOnlinePayment();
              } else {
                this.showToast(MessageConstants.TRY_LATER);
              }
            } else {
              this.showError({ title: "No vendor is present." });
            }
          }
        });
    },

    loadRazorpayScriptIfNeededOrContinueOnlinePayment() {
      if (!this.razorpayScriptLoaded) {
        const outerContext = this;
        this.loading = true;
        const razorpayScript = document.createElement("script");
        razorpayScript.setAttribute("src", environment.razorpayScriptUrl);
        razorpayScript.setAttribute("type", "text/javascript");
        document.head.appendChild(razorpayScript);
        razorpayScript.onload = function () {
          outerContext.loading = false;
          outerContext.razorpayScriptLoaded = true;
          outerContext.proceedOnlinePaymentOnRazorpay();
        };
        razorpayScript.onerror = function () {
          outerContext.loading = false;
          outerContext.showToast("Something went wrong. Please try again.");
        };
      } else {
        this.proceedOnlinePaymentOnRazorpay();
      }
    },

    proceedOnlinePaymentOnRazorpay() {
      let outerContext = this;
      const razorpayPaymentConfiguration = {
        key: this.onlinePaymentConfiguration.key,
        amount: this.onlinePaymentConfiguration.amount,
        currency: this.onlinePaymentConfiguration.currency,
        order_id: this.onlinePaymentConfiguration.orderId,
        name: this.onlinePaymentConfiguration.organizationName,
        description: this.onlinePaymentConfiguration.paymentDescription,
        image: this.onlinePaymentConfiguration.organizationLogoImage,
        prefill: {
          name: this.onlinePaymentConfiguration.customerName,
          email: this.onlinePaymentConfiguration.email,
          contact: this.onlinePaymentConfiguration.mobile
        },
        notes: {},
        theme: {
          color: "#2074f3"
        },
        handler: function (razorpayPaymentResponse) {
          outerContext.onRazorpayPaymentResponseReceived(razorpayPaymentResponse);
        }
      };

      // eslint-disable-next-line no-undef
      const razorpay = new Razorpay(razorpayPaymentConfiguration);
      razorpay.open();
    },

    onRazorpayPaymentResponseReceived(razorpayPaymentResponse) {
      this.loading = true;
      DataSource.verifySignature(
        LoanScopeDataHolder.getLoanId(),
        razorpayPaymentResponse.razorpay_order_id,
        razorpayPaymentResponse.razorpay_payment_id,
        razorpayPaymentResponse.razorpay_signature
      )
        .then(paymentVerifyRes => {
          this.loading = false;
          if (paymentVerifyRes.errorOccurred) {
            this.showError(paymentVerifyRes.data);
          } else {
            const paymentVerifyData = paymentVerifyRes.data ? paymentVerifyRes.data : {};
            this.onlinePaymentStatus = paymentVerifyData.success;
            if (this.onlinePaymentStatus) {
              this.showToast(MessageConstants.PAYMENT_SUCCESSFUL);
            } else {
              this.showToast(paymentVerifyData.errorMessage);
            }
          }
        })
    },

    onSubmit() {
      if (this.checkForValidation()) {
        this.completeCurrentTaskAndGetNextTask();
      }
    },

    checkForValidation() {
      if (this.downpaymentAmount !== 0
        && (this.onlinePaymentAllowed || this.manualPaymentAllowed)
        && (!this.onlinePaymentStatus && !this.cashPaymentStatus)
      ) {
        this.showToast(MessageConstants.PAYMENT_IS_NOT_DONE);
        return false;
      }

      return true;
    },

    completeCurrentTaskAndGetNextTask() {
      this.loading = true;
      LoanTasksActivityNavigator.getNextTask(this.tasks)
        .then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data)
          }
        });
    },

    fetchInitialData() {
      this.showRefreshItem = false;
      this.loading = true;
      LoanTasksUtility.getLoanStageConfigData(
        LoanScopeDataHolder.getLoanId(),
        this.currentTaskName
      )
        .then(stageConfigRes => {
          this.loading = false;
          if (stageConfigRes.errorOccurred) {
            this.showError(stageConfigRes.data);
            this.showRefreshItem = true;
          } else {
            const stageConfigData = stageConfigRes.data ? stageConfigRes.data : {};
            if (stageConfigData.completeOnLenderPlatform) {
              const lenderPlatformDetails = stageConfigData.lenderPlatformDetails;
              if (lenderPlatformDetails && lenderPlatformDetails.message) {
                this.lenderPlatformMessage = lenderPlatformDetails.message;
              }
            } else {
              let hasValidRole = false;
              let allowedRoles = "";
              stageConfigData.allowedRoles && stageConfigData.allowedRoles.forEach(
                allowedRole => {
                  allowedRoles = allowedRoles ? `${allowedRoles}, ${allowedRole}` : allowedRole;
                  if (PrefManager.doesUserHasRole(allowedRole)) {
                    hasValidRole = true;
                  }
                });
              if (!hasValidRole) {
                this.goToHomePageMessage = `Downpayment needs to be done by ${allowedRoles}. Please contact support for any help.`;
                this.showGoToHomePageDialog = true;
                return;
              }

              this.showUI = true;

              this.onlinePaymentAllowed = stageConfigData.onlinePaymentAllowed;
              this.manualPaymentAllowed = stageConfigData.manualPaymentAllowed;
              this.onlinePaymentStatus = stageConfigData.onlinePaymentStatus;
              this.downpaymentAmount = stageConfigData.downpaymentAmount
                ? Number(stageConfigData.downpaymentAmount).toFixed(2)
                : 0;
            }
          }
        })
    }
  },

  created() {
    this.fetchInitialData();
  },

  computed: {
    redirectActivity() {
      return Activities.LOAN_DETAILS;
    },

    tasks() {
      return [Activities.DOWNPAYMENT];
    },

    currentTaskName() {
      return LoanScopeDataHolder.getCurrentLoanTask()
        ? LoanScopeDataHolder.getCurrentLoanTask().name
        : "";
    },

    rupeeSign() {
      return RUPEE;
    },

    paymentWithCashCheckboxLink() {
      return this.cashPaymentStatus
        ? ImageResourcesUtil.getImageLink(ImageResourcesUtil.CHECKED_CHECKBOX)
        : ImageResourcesUtil.getImageLink(ImageResourcesUtil.UNCHECKED_CHECKBOX);
    }
  }
};
</script>

<style scoped src="../../assets/style/base.css"></style>


